// @flow
import type { EventSink } from '../event-sink';
import type { UserEvent } from '../user-event';
import {
  INTERACTION,
  NAVIGATE,
  REDUX_ACTION,
  WHITEBOARD_REDUX_ACTION,
} from '../user-event';

import { today } from 'modules/date';
import { appendScriptToHead, scriptTagWithJS } from 'modules/dom';
import type { User } from 'modules/user';
import { identifierAsString, asIdentifier } from 'modules/ecmascript';
import type { Identifier } from 'modules/ecmascript';

export type Specification = $ReadOnly<{|
  /**
   * Google Analytics Measurement ID.
   *
   * @see https://analytics.google.com/analytics/web/ Admin -> Tracking Info -> Tracking Code
   */
  gaMeasurementId: string,
  dataLayerName: string,
|}>;

export type Options = $ReadOnly<{|
  /**
   * Google Analytics Measurement ID.
   *
   * @see https://analytics.google.com/analytics/web/ Admin -> Tracking Info -> Tracking Code
   */
  gaMeasurementId: string,
  dataLayerName: Identifier,
|}>;

export type GenericEvent = $ReadOnly<{ ... }>;

export type PageViewEvent = $ReadOnly<{|
  page_title?: string,
  page_location?: string,
  page_path: string,
  send_to?: string,
|}>;

export type GoogleAnalyticsEvent = GenericEvent | PageViewEvent;

export const userEventAsGoogleAnalyticsEvent: (UserEvent) => [
  string,
  GoogleAnalyticsEvent,
] = (userEvent) => {
  switch (userEvent.type) {
    case NAVIGATE:
      return [
        'page_view',
        {
          page_path: userEvent.payload.to,
        },
      ];
    case INTERACTION:
      return (({ interaction, ...rest }: { +interaction: string, ... }) => [
        interaction,
        rest,
      ])(userEvent.payload);
    case REDUX_ACTION:
      return [(userEvent.payload: any).type, { ...null }];
    case WHITEBOARD_REDUX_ACTION:
      return ['whiteboard_action', { type: (userEvent.payload: any).type }];
    default:
      (userEvent.type: empty);
      throw new Error(`Unhandled UserEvent type: ${userEvent.type}`);
  }
};

export const dataLayerJS = ({ dataLayerName }: Options): string =>
  `
window.${identifierAsString(dataLayerName)} = window.${identifierAsString(
    dataLayerName,
  )} || [];
`;

export const gtagScript = ({
  gaMeasurementId,
  dataLayerName,
}: Options): HTMLScriptElement => {
  const scriptTag = document.createElement('script');
  // eslint-disable-next-line fp/no-mutation
  scriptTag.async = true;
  // eslint-disable-next-line fp/no-mutation
  scriptTag.src = `https://www.googletagmanager.com/gtag/js?id=${gaMeasurementId}&l=${identifierAsString(
    dataLayerName,
  )}`;

  return scriptTag;
};

export const initializeGoogleAnalyticsGtag = (
  options: Options,
): Array<mixed> => {
  appendScriptToHead(gtagScript(options));
  appendScriptToHead(scriptTagWithJS(dataLayerJS(options)));

  return window[options.dataLayerName];
};

class GoogleAnalyticsGtagEventSink implements EventSink {
  specification: Specification;

  dataLayerName: Identifier;

  dataLayer: Array<mixed>;

  user: User | null;

  constructor(specification: Specification) {
    // eslint-disable-next-line fp/no-mutation
    this.specification = specification;
    // eslint-disable-next-line fp/no-mutation
    this.dataLayerName = asIdentifier(specification.dataLayerName);
    // eslint-disable-next-line fp/no-mutation
    this.dataLayer = initializeGoogleAnalyticsGtag({
      ...this.specification,
      dataLayerName: this.dataLayerName,
    });

    this.pushStuffToDataLayer('js', today());

    this.configure();
  }

  pushStuffToDataLayer(..._args: $ReadOnlyArray<any>) {
    // apparently google _really_ wants this to be an arguments object, a regular array won't
    // trigger collection.
    // eslint-disable-next-line fp/no-mutating-methods, fp/no-arguments, prefer-rest-params
    this.dataLayer.push(arguments);
  }

  configure() {
    this.pushStuffToDataLayer('config', this.specification.gaMeasurementId, {
      // eslint-disable-next-line no-underscore-dangle
      user_id: this.user ? this.user._id : null,
    });
  }

  setUser(user: User | null) {
    // eslint-disable-next-line fp/no-mutation
    this.user = user;

    this.configure();
  }

  handleEvent(event: UserEvent, user: User | null) {
    if (user && !this.user) this.setUser(user);
    // eslint-disable-next-line fp/no-mutating-methods
    this.pushStuffToDataLayer(
      'event',
      ...userEventAsGoogleAnalyticsEvent(event),
    );
  }
}

export const fromSpecification: (Specification) => GoogleAnalyticsGtagEventSink =
  (specification) => new GoogleAnalyticsGtagEventSink(specification);

export default GoogleAnalyticsGtagEventSink;
